import { useCallback } from 'react';

import { LazyAutocompleteFieldNotMultiple } from '@occc/ui-design-system';
import { useGetGeneralSurveysLazy } from '@occc-app/dataAccessLayer/survey/queries';
import { GeneralSurveyFragment, SurveyFilter, SurveyStatus, Survey_Type } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';

const NUMBER_OF_ITEMS_TO_LOAD = 50;

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetGeneralSurveysLazy>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (item: GeneralSurveyFragment) => ({
  value: item.uuid,
  label: item.name,
  _raw: item,
});

export type SurveysSelectProps = {
  name: string;
  label: string;
  onChange?: (value: GeneralSurveyFragment | null) => void;
  surveyFilterOverrides?: SurveyFilter;
};

export const SurveysSelect = ({
  name,
  label,
  onChange,
  surveyFilterOverrides = {},
}: SurveysSelectProps) => {
  const chainId = useChainId();

  const makeQueryFilter: (search: string) => SurveyFilter = useCallback(
    (search) => ({
      search: search ? search : undefined,
      typeEquals: Survey_Type.General,
      statusEquals: SurveyStatus.Active,
      ...surveyFilterOverrides,
      chainIdEquals: chainId,
    }),
    [chainId, surveyFilterOverrides],
  );

  return (
    <LazyAutocompleteFieldNotMultiple<
      GeneralSurveyFragment,
      ReturnType<typeof useGetGeneralSurveysLazy>['response']
    >
      name={name}
      label={label}
      useLazyQuery={useGetGeneralSurveysLazy}
      makeQueryFilter={makeQueryFilter}
      mapQueryResponseToRawArray={mapQueryResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
      inputProps={{ margin: 'none' }}
      first={NUMBER_OF_ITEMS_TO_LOAD}
      onChange={onChange}
    />
  );
};
